import { cloneDeep, isEqual as lodashIsEqual } from 'lodash';

/**
 * Determines whether a given object is valued by checking to ensure it isn't <code>null</code> and not
 * <code>undefined</code>.
 *
 * @param toCheck the object to check for a value.
 *
 * @returns true if the given object is not <code>null</code> and not <code>undefined</code>, false if it is either
 * <code>null</code> or <code>undefined</code>.
 */
export function isValued(toCheck: unknown): boolean {
  return toCheck !== undefined && toCheck !== null;
}

/**
 * Determines whether a given object is not valued by checking to ensure it is either <code>null</code> or
 * <code>undefined</code>.
 *
 * @param toCheck the object to check for emptiness.
 *
 * @returns true if the given object is <code>null</code> or <code>undefined</code>, false if it has any value.
 */
export function isNotValued(toCheck: unknown): boolean {
  return !isValued(toCheck);
}

/**
 * Determine whether or not two objects are considered equal by way of a deep comparison.  For arrays, it will
 * check if the count, order and value of objects are the same (matching objects must have the same array index).
 *
 * @param objectA on of the objects to compare.
 * @param objectB the other object to compare.
 *
 * @returns true if the given pair of objects are considered equal, false otherwise.
 */
export function areEqual(objectA: any, objectB: any): boolean {
  return lodashIsEqual(objectA, objectB);
}

/**
 * Determine whether or not two objects are considered NOT equal by way of a deep comparison.  For arrays, it will
 * check if the count, order and value of objects are the same (matching objects must have the same array index).
 *
 * @param objectA on of the objects to compare.
 * @param objectB the other object to compare.
 *
 * @returns true if the given pair of objects are considered NOT equal, false otherwise.
 */
export function areNotEqual(objectA: any, objectB: any): boolean {
  return !areEqual(objectA, objectB);
}

/**
 * Creates a deep copy (all levels) of the given object.
 *
 * @param toCopy the object to copy.
 *
 * @return a deep copy of the given object.
 */
export function deepCopy<T>(toCopy: T): T {
  return cloneDeep<T>(toCopy);
}
